import PropTypes from "prop-types";
import React, { useState } from "react";
import { Alert, Badge, Button, EmptyState, Menu, Modal, ProgressBar, Table, Tag, TextInput, Toggle, DotsVertical, Edit01, Plus, SearchSm, Trash01, UsersPlus } from "@opendate-app/setlist";
import { toastError, toastSuccess } from "../../../../../shared/toastHelper";
import { addGuests, removeGuest, updateGuest, updateGuestList } from "./guestListApi";
import AddGuestsModal from "./AddGuestsModal";
import EditGuestModal from "./EditGuestModal";

const LIST_DOT_COLORS = ["#1982C4", "#5F5383", "#0E9384", "#FF7A00"];
const ALL_LISTS = "all";

const pluralize = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const formatAdded = (guest) => {
  const date = new Date(guest.added_at).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return guest.added_by ? `${date} · ${guest.added_by}` : date;
};

const checkInStatus = (guest) => {
  if (guest.checked_in === 0) return "Not yet";
  if (guest.checked_in < guest.tickets) return `${guest.checked_in} of ${guest.tickets}`;
  return guest.tickets > 1 ? "All in" : "In";
};

const GuestListCard = ({ teamId, confirmId, guestList, onGuestListChange }) => {
  const [filter, setFilter] = useState(ALL_LISTS);
  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newGuestIds, setNewGuestIds] = useState([]);
  const [editingGuest, setEditingGuest] = useState(null);
  const [removingGuest, setRemovingGuest] = useState(null);

  const [savingCapacity, setSavingCapacity] = useState(false);
  const [pendingManifested, setPendingManifested] = useState(null);

  const { lists, guests, tickets_sold: ticketsSold, capacity, capacity_remaining: capacityRemaining, can_remove: canRemove, manifested, can_change_capacity: canChangeCapacity } = guestList;
  const dotColor = (listName) => LIST_DOT_COLORS[lists.findIndex((list) => list.name === listName) % LIST_DOT_COLORS.length] || LIST_DOT_COLORS[0];
  const totalTickets = guests.reduce((sum, guest) => sum + guest.tickets, 0);
  const totalCheckedIn = guests.reduce((sum, guest) => sum + guest.checked_in, 0);
  const listNamesWithGuests = [...new Set([...lists.map((list) => list.name), ...guests.map((guest) => guest.list_name)])]
    .filter((listName) => guests.some((guest) => guest.list_name === listName));
  const normalizedQuery = query.trim().toLowerCase();
  const visibleGuests = guests.filter((guest) =>
    (filter === ALL_LISTS || guest.list_name === filter) &&
    (!normalizedQuery || guest.name.toLowerCase().includes(normalizedQuery))
  );

  const handleSubmit = ({ listName, text }) => {
    setSubmitting(true);
    const previousIds = new Set(guests.map((guest) => guest.id));
    addGuests({ teamId, confirmId, listName, text })
      .then(({ data }) => {
        const added = data.guests.filter((guest) => !previousIds.has(guest.id));
        onGuestListChange(data);
        setNewGuestIds(added.map((guest) => guest.id));
        setFilter(ALL_LISTS);
        setQuery("");
        setModalOpen(false);
        toastSuccess(`Added ${pluralize(added.length, "guest")} to ${listName === "House" ? "the house list" : `${listName}’s list`}`);
      })
      .catch((error) => toastError(error.response?.data?.errors?.join(", ") || "Couldn’t add guests."))
      .finally(() => setSubmitting(false));
  };

  const handleEdit = ({ name, listName }) => {
    setSubmitting(true);
    updateGuest({ teamId, confirmId, guestId: editingGuest.id, name, listName })
      .then(({ data }) => {
        onGuestListChange(data);
        setEditingGuest(null);
        toastSuccess(`Updated ${name.trim()}`);
      })
      .catch((error) => toastError(error.response?.data?.errors?.join(", ") || "Couldn’t update this guest."))
      .finally(() => setSubmitting(false));
  };

  const saveManifested = (countsTowardCapacity) => {
    setSavingCapacity(true);
    updateGuestList({ teamId, confirmId, manifested: countsTowardCapacity })
      .then(({ data }) => {
        onGuestListChange(data);
        setPendingManifested(null);
        toastSuccess(countsTowardCapacity ? "Guest list tickets now count toward capacity" : "Guest list tickets no longer count toward capacity");
      })
      .catch((error) => toastError(error.response?.data?.errors?.join(", ") || "Couldn’t update the guest list."))
      .finally(() => setSavingCapacity(false));
  };

  const handleManifestedChange = (event) => {
    if (totalTickets === 0) {
      saveManifested(event.target.checked);
    } else {
      setPendingManifested(event.target.checked);
    }
  };

  const overCapacityBy = capacityRemaining === null || capacityRemaining === undefined ? 0 : totalTickets - capacityRemaining;

  const handleRemove = () => {
    setSubmitting(true);
    removeGuest({ teamId, confirmId, guestId: removingGuest.id })
      .then(({ data }) => {
        onGuestListChange(data);
        setRemovingGuest(null);
        toastSuccess(`Removed ${removingGuest.name} from the guest list`);
      })
      .catch((error) => toastError(error.response?.data?.errors?.join(", ") || "Couldn’t remove this guest."))
      .finally(() => setSubmitting(false));
  };

  const columns = [
    {
      key: "name",
      header: "Guest",
      primary: true,
      render: (guest) => (
        <span className="d2-guest-name">
          <strong>{guest.name}</strong>
          {guest.tickets > 1 && <Badge size="sm" color="gray">{`+${guest.tickets - 1}`}</Badge>}
          {newGuestIds.includes(guest.id) && <Badge size="sm" color="brand">New</Badge>}
        </span>
      ),
    },
    {
      key: "list",
      header: "List",
      render: (guest) => (
        <span className="d2-guest-list-name">
          <span className="d2-guest-dot" style={{ background: dotColor(guest.list_name) }} />
          {guest.list_name}
        </span>
      ),
    },
    { key: "tickets", header: "Tickets", align: "right", render: (guest) => guest.tickets },
    {
      key: "checkedIn",
      header: "Checked in",
      render: (guest) => (
        <span className="d2-guest-check-in">
          <span className="d2-guest-pips">
            {Array.from({ length: guest.tickets }, (_, index) => (
              <span key={index} className={index < guest.checked_in ? "is-in" : ""} />
            ))}
          </span>
          <span className={guest.checked_in ? "is-strong" : ""}>{checkInStatus(guest)}</span>
        </span>
      ),
    },
    { key: "added", header: "Added", render: formatAdded },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (guest) => (
        <Menu
          placement="bottom-end"
          items={[
            { label: "Edit guest", icon: Edit01, onClick: () => setEditingGuest(guest) },
            ...(canRemove ? [{
              label: "Remove from list",
              icon: Trash01,
              destructive: true,
              disabled: guest.checked_in > 0,
              description: guest.checked_in > 0 ? "Already checked in" : undefined,
              onClick: () => setRemovingGuest(guest),
            }] : []),
          ]}
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${guest.name}`} />}
        />
      ),
    },
  ];

  return (
    <div className="d2-card d2-guest-list">
      <div className="d2-guest-header">
        <div>
          <div className="d2-guest-title">
            <span className="d2-card-title">Guest list</span>
            {guests.length > 0 && (
              <Badge color="gray">{`${pluralize(guests.length, "guest")} · ${pluralize(totalTickets, "ticket")}`}</Badge>
            )}
          </div>
          <p className="d2-guest-subtitle">
            Comps on the <strong>Guest List</strong> ticket type. Guests check in by name at the door.
          </p>
        </div>
        <div className="d2-guest-header-actions">
          <Toggle
            id="guest-list-manifested"
            size="sm"
            label="Counts toward capacity"
            checked={manifested}
            disabled={!canChangeCapacity || savingCapacity}
            onChange={handleManifestedChange}
          />
          {guests.length > 0 && (
            <TextInput
              size="sm"
              leadingIcon={SearchSm}
              placeholder="Search guests"
              aria-label="Search guests"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          )}
          <Button variant="primary" icon={Plus} onClick={() => setModalOpen(true)}>Add guests</Button>
        </div>
      </div>

      {guests.length === 0 ? (
        <EmptyState
          size="sm"
          icon={UsersPlus}
          title="No guests yet"
          description="Paste the list from the artist’s team — one name per line, +2 for plus-ones. Names are all you need."
          primaryAction={{ label: "Add guests", icon: Plus, onClick: () => setModalOpen(true) }}
        />
      ) : (
        <>
          <div className="d2-guest-toolbar">
            <div className="d2-guest-filters">
              <Tag selectable selected={filter === ALL_LISTS} count={guests.length} onSelectedChange={() => setFilter(ALL_LISTS)}>
                All lists
              </Tag>
              {listNamesWithGuests.map((listName) => (
                <Tag
                  key={listName}
                  selectable
                  selected={filter === listName}
                  count={guests.filter((guest) => guest.list_name === listName).length}
                  onSelectedChange={() => setFilter(listName)}
                >
                  {listName}
                </Tag>
              ))}
            </div>
            <div className="d2-guest-progress">
              <span><strong>{totalCheckedIn}</strong>{` of ${totalTickets} checked in`}</span>
              <ProgressBar value={totalTickets ? Math.round((totalCheckedIn / totalTickets) * 100) : 0} label="none" />
            </div>
          </div>
          <Table
            columns={columns}
            data={visibleGuests}
            getRowKey={(guest) => guest.id}
            empty={<p className="d2-guest-no-match">{`No guests match “${query}”.`}</p>}
          />
        </>
      )}

      {modalOpen && (
        <AddGuestsModal
          teamId={teamId}
          confirmId={confirmId}
          lists={lists}
          ticketsSold={ticketsSold}
          capacity={capacity}
          manifested={manifested}
          submitting={submitting}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
        />
      )}

      {editingGuest && (
        <EditGuestModal
          guest={editingGuest}
          lists={lists}
          submitting={submitting}
          onClose={() => setEditingGuest(null)}
          onSubmit={handleEdit}
        />
      )}

      {pendingManifested !== null && (
        <Modal
          open
          onClose={() => setPendingManifested(null)}
          icon={false}
          title={pendingManifested ? "Count guests toward capacity?" : "Put guests on top of capacity?"}
          footer={(
            <div className="d2-guest-modal-actions">
              <Button variant="secondary" onClick={() => setPendingManifested(null)} disabled={savingCapacity}>Cancel</Button>
              <Button variant="primary" onClick={() => saveManifested(pendingManifested)} disabled={savingCapacity}>
                {pendingManifested ? "Count toward capacity" : "Put on top of capacity"}
              </Button>
            </div>
          )}
        >
          {pendingManifested ? (
            <>
              <p>
                {`The ${pluralize(totalTickets, "guest ticket")} on this list will count toward the event's capacity.`}
                {capacityRemaining !== null && capacityRemaining !== undefined &&
                  ` ${pluralize(capacityRemaining, "ticket")} ${capacityRemaining === 1 ? "is" : "are"} still available to sell.`}
              </p>
              {overCapacityBy > 0 && (
                <Alert
                  color="warning"
                  title={`This puts the event ${pluralize(overCapacityBy, "ticket")} over capacity`}
                  description="Every ticket will show as sold out until guests are removed or capacity is raised."
                />
              )}
            </>
          ) : (
            <p>{`The ${pluralize(totalTickets, "guest ticket")} on this list will stop counting toward capacity, which frees up ${totalTickets} more ${totalTickets === 1 ? "ticket" : "tickets"} to sell.`}</p>
          )}
        </Modal>
      )}

      {removingGuest && (
        <Modal
          open
          onClose={() => setRemovingGuest(null)}
          icon={false}
          title={`Remove ${removingGuest.name}?`}
          footer={(
            <div className="d2-guest-modal-actions">
              <Button variant="secondary" onClick={() => setRemovingGuest(null)} disabled={submitting}>Cancel</Button>
              <Button variant="destructive" onClick={handleRemove} disabled={submitting}>Remove guest</Button>
            </div>
          )}
        >
          <p>{`Their ${pluralize(removingGuest.tickets, "comp ticket")} will be voided and can no longer be scanned at the door.`}</p>
        </Modal>
      )}
    </div>
  );
};

GuestListCard.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  confirmId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  guestList: PropTypes.shape({
    lists: PropTypes.array.isRequired,
    guests: PropTypes.array.isRequired,
    tickets_sold: PropTypes.number.isRequired,
    capacity: PropTypes.number.isRequired,
    can_remove: PropTypes.bool,
    capacity_remaining: PropTypes.number,
    manifested: PropTypes.bool.isRequired,
    can_change_capacity: PropTypes.bool,
  }).isRequired,
  onGuestListChange: PropTypes.func.isRequired,
};

export default GuestListCard;
